import { z } from "zod";
import { auditInfoSchema } from "../common/schemas";
import { assetViewSchema, type AssetUpsertPayload } from "../assets/types";
import { assetAssignmentViewSchema } from "../asset-assignments/types";

export const workspaceTypes = ["Desk", "Office", "Room"] as const;
export type WorkspaceType = (typeof workspaceTypes)[number];

/**
 * Canonicalize an arbitrary workspace-type string to the PascalCase
 * form the rest of the frontend speaks. The backend serializes enums
 * as camelCase ("desk", "office", "room") on the wire, but the form
 * `<Select>` and `formatWorkspaceDisplay` expect the PascalCase
 * literals from `workspaceTypes`. Saves already round-trip cleanly
 * because the API's `TryParse` is case-insensitive — without this
 * normalizer the only visible breakage was on display.
 *
 * Unrecognized values pass through untouched so anything outside the
 * enum (e.g. a typo in a CSV row) stays visible for the user to fix.
 */
export function canonicalWorkspaceType(raw: string): string {
    const match = workspaceTypes.find((t) => t.toLowerCase() === raw.toLowerCase());
    return match ?? raw;
}

export const workspaceViewSchema = z.object({
    id: z.string(),
    sageOfficeId: z.string(),
    type: z.string().transform(canonicalWorkspaceType),
    name: z.string(),
    // Optional alternate label. Backend sends `null` when unset; we
    // accept both null and missing for forward-compat with old payloads
    // captured before the field existed.
    friendlyName: z.string().nullable().optional(),
    // Optional SVG element id on the office floorplan (e.g. Desk001).
    floorplanElementId: z.string().nullable().optional(),
    currentAssignmentId: z.string().nullable().optional(),
    created: auditInfoSchema.nullable().optional(),
    modified: auditInfoSchema.nullable().optional(),
    deleted: auditInfoSchema.nullable().optional(),
    isDeleted: z.boolean(),
});
export type WorkspaceView = z.infer<typeof workspaceViewSchema>;

export const workspaceListResponseSchema = z.object({
    results: z.array(workspaceViewSchema),
});

export const workspaceMutationResponseSchema = z.object({
    workspace: workspaceViewSchema,
});

export interface WorkspaceUpsertPayload {
    readonly id: string;
    readonly sageOfficeId: string;
    readonly type: string;
    readonly name: string;
    /**
     * Optional alternate label. The form sends `null` to mean
     * "explicitly cleared" and the backend collapses empty/whitespace
     * to null on the server side too. So callers should pass `null`
     * to clear, the trimmed string to set, and never an empty string.
     */
    readonly friendlyName: string | null;
    /**
     * Optional SVG element id on the office floorplan. Same null/clear
     * semantics as friendlyName.
     */
    readonly floorplanElementId: string | null;
}

/**
 * Wire shape for the workspace-anchored "add asset" call. Matches
 * `AddAssetToWorkspaceRequest` on the API. The asset id and assignment id
 * are both client-generated UUIDs so a network retry replays cleanly
 * (the server uses them as the idempotency keys).
 */
export interface AddAssetToWorkspacePayload {
    readonly asset: AssetUpsertPayload;
    readonly assignmentId: string;
}

export const addAssetToWorkspaceResponseSchema = z.object({
    asset: assetViewSchema,
    assignment: assetAssignmentViewSchema,
});
export type AddAssetToWorkspaceResult = z.infer<typeof addAssetToWorkspaceResponseSchema>;

/**
 * Wire shape for the bulk-import endpoint. Each row carries a
 * client-generated UUID so a retry of the whole file replays the
 * same ids — the backend's id-conflict path becomes "skipped" and
 * the operator can keep retrying until 0 failures without producing
 * duplicates.
 */
export interface BulkWorkspaceRow {
    readonly id: string;
    readonly sageOfficeId: string;
    readonly type: string;
    readonly name: string;
    readonly friendlyName: string | null;
    readonly floorplanElementId: string | null;
}

export interface BulkImportWorkspacesPayload {
    readonly items: readonly BulkWorkspaceRow[];
}

export const bulkRowResultSchema = z.object({
    rowIndex: z.number(),
    outcome: z.enum(["created", "skipped", "failed"]),
    errorMessage: z.string().nullable().optional(),
    id: z.string(),
});
export type BulkRowResult = z.infer<typeof bulkRowResultSchema>;

export const bulkImportWorkspacesResponseSchema = z.object({
    totalRows: z.number(),
    created: z.number(),
    skipped: z.number(),
    failed: z.number(),
    results: z.array(bulkRowResultSchema),
});
export type BulkImportWorkspacesResult = z.infer<typeof bulkImportWorkspacesResponseSchema>;

/**
 * Mirror of the backend hard cap. Held here so the SPA can refuse
 * an over-large file at parse time and tell the user up-front rather
 * than letting them upload, wait, and then receive a 400.
 */
export const BULK_IMPORT_WORKSPACES_MAX_ROWS = 1000;

/**
 * Per-asset outcome of "Reassign All". Closed vocabulary mirroring
 * `ReassignedAssetResult` on the API — an unknown value fails the parse
 * rather than being silently treated as success.
 */
export const reassignedAssetResultSchema = z.object({
    assetId: z.string(),
    outcome: z.enum(["moved", "failed"]),
    newAssignmentId: z.string().nullable().optional(),
    errorMessage: z.string().nullable().optional(),
});
export type ReassignedAssetResult = z.infer<typeof reassignedAssetResultSchema>;

export const reassignWorkspaceAssetsResponseSchema = z.object({
    sourceWorkspaceId: z.string(),
    targetWorkspaceId: z.string(),
    moved: z.number(),
    failed: z.number(),
    /** Source still had open assignments beyond the per-request cap. */
    hasMore: z.boolean(),
    results: z.array(reassignedAssetResultSchema),
});
export type ReassignWorkspaceAssetsResult = z.infer<typeof reassignWorkspaceAssetsResponseSchema>;

// ── Floorplan (reuses WorkspaceView; no separate domain model) ─────────────

/**
 * One mapped space on an office floorplan. Matches
 * `FloorplanSpaceView` on the API — workspace row + optional occupant.
 */
export const floorplanSpaceSchema = z.object({
    workspace: workspaceViewSchema,
    occupantUserId: z.string().nullable().optional(),
});
export type FloorplanSpaceView = z.infer<typeof floorplanSpaceSchema>;

export const officeFloorplanResponseSchema = z.object({
    sageOfficeId: z.string(),
    spaces: z.array(floorplanSpaceSchema),
});
export type OfficeFloorplanView = z.infer<typeof officeFloorplanResponseSchema>;

/** Map marker derived from a floorplan space / SVG element. */
export interface FloorplanMarker {
    readonly elementId: string;
    readonly occupied: boolean;
    readonly kind: "desk" | "room";
    /**
     * Optional on-map label. Prefer assigned user name, then friendly
     * name; vacant / unlabeled groups fall back to the SVG group id.
     */
    readonly label?: string | null;
}

/** Rich hover card content for a map shape. */
export interface FloorplanHoverInfo {
    readonly title: string;
    readonly statusLabel: string;
    readonly rows: readonly { readonly label: string; readonly value: string }[];
}

export interface FloorplanStats {
    readonly totalDesks: number;
    readonly occupied: number;
    readonly vacant: number;
}

export function computeFloorplanStats(markers: readonly FloorplanMarker[]): FloorplanStats {
    let totalDesks = 0;
    let occupied = 0;
    for (const m of markers) {
        if (m.kind !== "desk") continue;
        totalDesks += 1;
        if (m.occupied) occupied += 1;
    }
    return {
        totalDesks,
        occupied,
        vacant: totalDesks - occupied,
    };
}

/** Desk vs everything-else for floorplan overlays (uses shared workspace type). */
export function workspaceKind(workspace: WorkspaceView): "desk" | "room" {
    return workspace.type.toLowerCase() === "desk" ? "desk" : "room";
}

/**
 * SVG group id used to place a workspace on the floorplan.
 * Group ids are matched to workspace **name**. `floorplanElementId` is
 * only a legacy fallback for older rows.
 */
export function floorplanMapElementId(
    workspace: Pick<WorkspaceView, "name" | "floorplanElementId">,
): string | null {
    const fromName = workspace.name.trim();
    if (fromName) return fromName;
    const fromElement = workspace.floorplanElementId?.trim();
    return fromElement || null;
}

/**
 * Display title for a floorplan space / SVG group.
 * Priority: assigned user name → friendly name → group id
 * (workspace `name` matches the SVG group id).
 */
export function floorplanSpaceTitle(
    assignedUserName: string | null | undefined,
    friendlyName: string | null | undefined,
    groupId: string,
): string {
    const user = assignedUserName?.trim();
    if (user) return user;
    const friendly = friendlyName?.trim();
    if (friendly) return friendly;
    return groupId;
}
